/**
 * Sync PartyKit da organização: oportunidades, contatos, conversas e favoritos.
 */
import { useEffect, useRef, useCallback } from 'react';
import { createModuleLogger } from '../../../utils/logger';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAuth } from '../../../contexts/AuthContext';
import {
    PARTYKIT_CONNECTION_DEBOUNCE_MS,
    canUsePartykit,
    isValidPartykitHost,
    claimAuthedPartySocket,
    connectPartySocket,
    safeClosePartySocket,
    attachPartykitVisibilityReconnect,
    createPartykitCooldownController,
    attachPartySocketHandlers,
    emitPartyAssignmentChanged,
} from '../utils/partykitSocket';
import type PartySocket from 'partysocket';

const log = createModuleLogger('[PartykitSync]');
let hasLoggedDisabled = false;

interface SyncMessage {
    type: 'OPPORTUNITY_CHANGED' | 'CONTACT_CHANGED' | 'CONVERSATION_CHANGED' | 'FAVORITE_CHANGED' | 'CONNECTED';
    entityId?: string;
    entityType?: string;
    action?: 'INSERT' | 'UPDATE' | 'DELETE';
    timestamp: string;
    phone?: string;
    assignedTo?: string | null;
    organizationId?: string;
    status?: string;
    unreadCount?: number;
    remoteJid?: string;
    connectionId?: string;
    userId?: string;
}

interface UsePartykitSyncOptions {
    enabled?: boolean;
    onOpportunityChange?: () => void;
    onContactChange?: () => void;
    onConversationChange?: () => void;
}

type AssignmentBroadcast = {
    phone?: string;
    assignedTo?: string | null;
    organizationId?: string;
    status?: string;
    unreadCount?: number;
    remoteJid?: string;
    connectionId?: string;
};

export function usePartykitSync(options: UsePartykitSyncOptions = {}) {
    const { enabled = true } = options;
    const { currentOrganization } = useOrganization();
    const { user } = useAuth();
    const socketRef = useRef<PartySocket | null>(null);
    const isConnectedRef = useRef(false);
    const orgIdRef = useRef(currentOrganization?.id);
    orgIdRef.current = currentOrganization?.id;
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const sendSync = useCallback((partial: Omit<SyncMessage, 'timestamp'>) => {
        if (!socketRef.current || !isConnectedRef.current) return;
        socketRef.current.send(JSON.stringify({ ...partial, timestamp: new Date().toISOString() }));
        log.log('📡 Broadcast enviado:', partial.type, partial.entityId ?? partial.phone);
    }, []);

    const broadcastOpportunityChange = useCallback(
        (id?: string) => sendSync({ type: 'OPPORTUNITY_CHANGED', entityId: id }),
        [sendSync],
    );
    const broadcastConversationChange = useCallback(
        (id?: string, assignment?: AssignmentBroadcast) =>
            sendSync({ type: 'CONVERSATION_CHANGED', entityId: id, ...assignment }),
        [sendSync],
    );
    const broadcastFavoriteChange = useCallback((
        conversationId: string,
        action: 'INSERT' | 'DELETE',
        userId?: string,
    ) => sendSync({
        type: 'FAVORITE_CHANGED',
        entityId: conversationId,
        action,
        userId,
        organizationId: orgIdRef.current,
    }), [sendSync]);

    useEffect(() => {
        if (!enabled) {
            safeClosePartySocket(socketRef.current);
            socketRef.current = null;
            isConnectedRef.current = false;
            return;
        }
        if (!isValidPartykitHost()) {
            if (!hasLoggedDisabled) {
                log.log('⏭️ PartyKit Sync desabilitado (host não configurado)');
                hasLoggedDisabled = true;
            }
            return;
        }
        if (!currentOrganization?.id) return;

        let cancelled = false;
        let connectAttempt = 0;

        const handlePayload = (data: SyncMessage) => {
            if (data.type === 'CONNECTED') return;
            const opts = optionsRef.current;

            if (data.type === 'OPPORTUNITY_CHANGED') {
                opts.onOpportunityChange?.();
                return;
            }
            if (data.type === 'CONTACT_CHANGED') {
                opts.onContactChange?.();
                return;
            }
            if (data.type === 'CONVERSATION_CHANGED') {
                emitPartyAssignmentChanged(data);
                opts.onConversationChange?.();
                return;
            }
            if (data.type === 'FAVORITE_CHANGED') {
                window.dispatchEvent(new CustomEvent('favorite:changed', {
                    detail: {
                        conversationId: data.entityId,
                        userId: data.userId,
                        action: data.action,
                        organizationId: data.organizationId,
                        source: 'partykit',
                    },
                }));
            }
        };

        const connectSocket = () => {
            if (cancelled || !canUsePartykit('sync')) return;

            const attempt = ++connectAttempt;
            safeClosePartySocket(socketRef.current);
            socketRef.current = null;
            log.log('🔌 Conectando ao Partykit Sync para org:', currentOrganization.id);

            void (async () => {
                try {
                    const socket = await claimAuthedPartySocket(
                        currentOrganization.id,
                        'sync',
                        {
                            query: user?.id ? { user_id: user.id } : undefined,
                            isCancelled: () => cancelled,
                            attempt,
                            latestAttempt: () => connectAttempt,
                        },
                    );
                    if (!socket) return;
                    socketRef.current = socket;
                    attachPartySocketHandlers(socket, 'sync', () => cancelled, {
                        onOpen: () => {
                            log.log('✅ Partykit Sync conectado!');
                            isConnectedRef.current = true;
                        },
                        onClose: () => {
                            isConnectedRef.current = false;
                            if (!canUsePartykit('sync') && !hasLoggedDisabled) {
                                log.debug('PartyKit Sync indisponível — usando polling');
                                hasLoggedDisabled = true;
                            }
                        },
                        armCooldown: () => cooldown.armAfterFailure(),
                        onMessage: (raw) => {
                            try {
                                handlePayload(JSON.parse(raw) as SyncMessage);
                            } catch (error) {
                                log.error('Erro ao processar mensagem:', error);
                            }
                        },
                    });
                    connectPartySocket(socket);
                } catch (error) {
                    log.debug('PartyKit Sync não inicializado:', error);
                }
            })();
        };

        const cooldown = createPartykitCooldownController('sync', connectSocket, () => cancelled);
        const debounceTimer = setTimeout(connectSocket, PARTYKIT_CONNECTION_DEBOUNCE_MS);
        const detachVisibility = attachPartykitVisibilityReconnect(
            'sync',
            () => socketRef.current,
            () => {
                log.log('🔄 Reconectando PartyKit Sync após retorno à aba...');
                connectSocket();
            },
            () => cancelled,
        );

        return () => {
            cancelled = true;
            clearTimeout(debounceTimer);
            cooldown.dispose();
            detachVisibility();
            safeClosePartySocket(socketRef.current);
            socketRef.current = null;
            isConnectedRef.current = false;
        };
    }, [enabled, currentOrganization?.id, user?.id]);

    return {
        broadcastOpportunityChange,
        broadcastConversationChange,
        broadcastFavoriteChange,
    };
}
